import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Header3 } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import content from "@iventis/translations/content";
import React, { FunctionComponent, useCallback, useState } from "react";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { layerSectionMixin, narrowPadding, sidebarPadding, styled } from "@iventis/styles";
import { ChangeLogEntry } from "@iventis/domain-model/model/changeLogEntry";
import { VirtualScrollingChangeLogComponent } from "@iventis/change-log";
import { mappingApi } from "@iventis/api/src/api";
import { PagedCursorResult } from "@iventis/domain-model/model/pagedCursorResult";
import { ClickAwayListenerToolTip, InfoTooltip } from "@iventis/components";
import { mapEntries } from "./plan-change-log-entry-mapper";
import { SidebarButton } from "../sidebar-button";

export const PlanChangeLogComponent: FunctionComponent<{ planId: string; onClose: () => void }> = ({ planId, onClose }) => {
    const translate = useIventisTranslate();
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [showInfo, setShowInfo] = useState(false);

    const handleRefresh = () => {
        setRefreshTrigger((current) => current + 1);
    };

    const requester = useCallback(
        async (cursorId?: string): Promise<PagedCursorResult<ChangeLogEntry[]>> => {
            if (!planId) {
                return {
                    pageSize: 0,
                    lastPage: true,
                    results: [],
                };
            }
            const cursorQuery = cursorId ? `&cursorId=${encodeURIComponent(cursorId)}` : "";
            const resp = await mappingApi.get<PagedCursorResult<ChangeLogEntry[]>>(`/change-log?planIdQuery=${encodeURIComponent(planId)}${cursorQuery}`);
            return resp.data;
        },
        [planId]
    );

    return (
        <StyledChangeLogContainer>
            <StyledNav>
                <StyledHeader>{translate(Content.changeLog.changeLogTitle)}</StyledHeader>
                <StyledSubContentActions>
                    <ClickAwayListenerToolTip onClickAway={() => setShowInfo(false)}>
                        <InfoTooltip
                            title={translate(Content.changeLog.help.title)}
                            text={
                                <StyledHelpList>
                                    <li>{translate(Content.changeLog.help.layersCreatedDeleted)}</li>
                                    <li>{translate(Content.changeLog.help.layerOperationsGrouped)}</li>
                                    <li>{translate(Content.changeLog.help.mapObjectsGrouped)}</li>
                                    <li>*{translate(Content.changeLog.help.mapObjectEditHint)}</li>
                                </StyledHelpList>
                            }
                            open={showInfo}
                            onClose={() => setShowInfo(false)}
                            placement="bottom-end"
                        >
                            <StyledSubContentActionButton
                                testId="change-log-info-button"
                                onClick={() => setShowInfo((current) => !current)}
                                toolTip={translate(Content.changeLog.help.tooltip)}
                                toolTipPlacement="bottom"
                            >
                                <StyledSubContentActionIcon icon={["far", "circle-info"]} />
                            </StyledSubContentActionButton>
                        </InfoTooltip>
                    </ClickAwayListenerToolTip>
                    <StyledSubContentActionButton testId="refresh-sub-content-button" onClick={handleRefresh} toolTip={translate(Content.map8.refresh)} toolTipPlacement="bottom">
                        <StyledSubContentActionIcon icon={["far", "arrows-rotate"]} />
                    </StyledSubContentActionButton>
                    <StyledSubContentActionButton
                        testId="back-to-layers-button"
                        onClick={onClose}
                        toolTip={translate(content.map5.toolbarButtonTooltips.layers)}
                        toolTipPlacement="bottom"
                    >
                        <StyledSubContentActionIcon icon={["far", "xmark"]} />
                    </StyledSubContentActionButton>
                </StyledSubContentActions>
            </StyledNav>
            <StyledChangeLogListContainer>
                <VirtualScrollingChangeLogComponent requester={requester} mapper={mapEntries} refreshTrigger={refreshTrigger} />
            </StyledChangeLogListContainer>
        </StyledChangeLogContainer>
    );
};

const StyledChangeLogContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const StyledChangeLogListContainer = styled.div`
    min-height: 0;
    flex: 1;
`;

const StyledNav = styled.nav`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${narrowPadding} ${sidebarPadding};
    border-bottom: ${({ theme }) => `1px solid ${theme.shades.four}`};
    ${layerSectionMixin}

    button {
        height: 100%;
        :hover {
            color: ${({ theme }) => theme.primaryColors.focusAccent};
        }
    }
`;

const StyledHeader = styled(Header3)`
    text-transform: capitalize;
    font-weight: 500;
    color: ${({ theme }) => theme.typographyColors.core};
`;

const StyledSubContentActions = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const StyledSubContentActionButton = styled(SidebarButton)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 15px;
    width: 15px;
    padding: 0;
`;

const StyledSubContentActionIcon = styled(FontAwesomeIcon)`
    height: 100%;
    width: 100%;
`;

const StyledHelpList = styled.ul`
    margin: 0;
    padding-left: 1.1rem;

    li {
        margin-bottom: 0.5rem;
    }

    li:last-child {
        margin-bottom: 0;
    }
`;
